// src/components/SearchBar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaSearch,
  FaImage,
  FaMicrophone,
  FaPaperclip,
  FaTimes,
  FaTrashAlt,
} from "react-icons/fa";
import "./SearchBar.css";

/**
 * Advanced, aura-glow Search Bar
 * - Paste-to-attach, drag & drop, tooltips
 * - Mini thumbnails above the bar (ChatGPT-style) [toggle via showMiniThumbs]
 * - Notifies parent of live attachments via onAttachmentsChange
 * - ✅ Auto-growing textarea (ChatGPT-style) controlled by `autoGrow` & `maxAutoGrowPx`
 */
export default function SearchBar({
  onSubmit,
  placeholder = "Type a message to start a fluid conversation…",
  maxFiles = 12,
  disabled = false,
  busy = false,
  onAttachmentsChange,           // parent receives {images[], audio[], docs[]}
  showMiniThumbs = true,         // allow parent to hide internal mini-chips
  autoGrow = true,               // NEW: expand the input as text gets longer (ChatGPT style)
  maxAutoGrowPx = 280,           // NEW: cap for auto-growth before scrolling kicks in
}) {
  const [query, setQuery] = useState("");
  const [attachments, setAttachments] = useState({
    images: [],
    audio: [],
    docs: [],
  });
  const [isDragging, setDragging] = useState(false);

  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const docInputRef   = useRef(null);
  const shellRef      = useRef(null);
  const inputRef      = useRef(null);    // textarea ref

  // Keep latest attachments for on-unmount cleanup
  const attachmentsRef = useRef(attachments);

  const totalCount = useMemo(
    () => attachments.images.length + attachments.audio.length + attachments.docs.length,
    [attachments]
  );
  const remainingSlots = useMemo(
    () => Math.max(0, maxFiles - totalCount),
    [maxFiles, totalCount]
  );

  const acceptDocs =
    ".pdf,.txt,.csv,.md,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.json,.yaml,.yml";

  const makePreview = (f) => {
    try { return URL.createObjectURL(f); } catch { return null; }
  };

  const extFromName = (n = "") => (n.split(".").pop() || "FILE").toUpperCase();

  function categorize(filesList) {
    const next = { images: [], audio: [], docs: [] };
    for (const file of filesList) {
      if (!file) continue;
      if (file.type.startsWith("image/")) next.images.push({ file, url: makePreview(file) });
      else if (file.type.startsWith("audio/")) next.audio.push({ file, url: makePreview(file) });
      else next.docs.push({ file, url: makePreview(file), ext: extFromName(file.name) });
    }
    return next;
  }

  const clampToSlots = (filesList) =>
    remainingSlots <= 0 ? [] : Array.from(filesList).slice(0, remainingSlots);

  const merge = (next) =>
    setAttachments((prev) => ({
      images: [...prev.images, ...next.images],
      audio:  [...prev.audio,  ...next.audio],
      docs:   [...prev.docs,   ...next.docs],
    }));

  const onPickImages = (e) => {
    merge(categorize(clampToSlots(e.target.files || [])));
    e.target.value = "";
  };
  const onPickAudio = (e) => {
    merge(categorize(clampToSlots(e.target.files || [])));
    e.target.value = "";
  };
  const onPickDocs = (e) => {
    merge(categorize(clampToSlots(e.target.files || [])));
    e.target.value = "";
  };

  // Notify parent + keep a ref snapshot whenever attachments change
  useEffect(() => {
    attachmentsRef.current = attachments;
    onAttachmentsChange?.(attachments);
  }, [attachments, onAttachmentsChange]);

  // Cleanup object URLs on unmount only (avoid revoking while still in use)
  useEffect(() => {
    return () => {
      const all = [
        ...attachmentsRef.current.images,
        ...attachmentsRef.current.audio,
        ...attachmentsRef.current.docs,
      ];
      all.forEach((a) => a?.url && URL.revokeObjectURL(a.url));
    };
  }, []);

  // Paste to attach
  useEffect(() => {
    const handler = (e) => {
      if (!e.clipboardData) return;
      const files = [];
      if (e.clipboardData.files?.length) {
        for (const f of e.clipboardData.files) files.push(f);
      } else {
        for (const item of e.clipboardData.items) {
          if (item.kind === "file") {
            const f = item.getAsFile();
            if (f) files.push(f);
          }
        }
      }
      if (files.length) merge(categorize(clampToSlots(files)));
    };
    const node = shellRef.current;
    node?.addEventListener("paste", handler);
    return () => node?.removeEventListener("paste", handler);
  }, [remainingSlots]);

  // Shortcuts: "/" focuses, Ctrl/Cmd+Enter submits
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && document.activeElement?.tagName !== "TEXTAREA" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Drag & drop
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    merge(categorize(clampToSlots(e.dataTransfer.files || [])));
  };

  const removeOne = (kind, idx) =>
    setAttachments((prev) => {
      const next = {
        images: [...prev.images],
        audio:  [...prev.audio],
        docs:   [...prev.docs],
      };
      const removed = next[kind].splice(idx, 1)[0];
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return next;
    });

  function clearAll() {
    const all = [...attachments.images, ...attachments.audio, ...attachments.docs];
    all.forEach((a) => a?.url && URL.revokeObjectURL(a.url));
    setAttachments({ images: [], audio: [], docs: [] });
  }

  function handleSubmit(e) {
    e?.preventDefault?.();
    if (disabled || busy) return;
    if (!query.trim() && totalCount === 0) return;

    onSubmit?.({
      query: query.trim(),
      files: {
        images: attachments.images.map((a) => a.file),
        audio:  attachments.audio.map((a) => a.file),
        docs:   attachments.docs.map((a) => a.file),
      },
    });

    // Clear input & attachments after send
    setQuery("");
    clearAll();
    inputRef.current?.focus();
    setDragging(false);
  }

  // ✅ Auto-grow the textarea so long prompts stay visible (ChatGPT-like)
  useEffect(() => {
    const ta = inputRef.current;
    if (!ta) return;

    if (!autoGrow) {
      ta.style.height = "";
      ta.style.overflowY = "";
      return;
    }

    // Reset then grow to content; cap at maxAutoGrowPx
    ta.style.height = "auto";
    const cap = Math.max(120, Number(maxAutoGrowPx) || 280); // enforce sensible minimum
    const next = Math.min(cap, ta.scrollHeight);
    ta.style.height = next + "px";
    ta.style.overflowY = ta.scrollHeight > cap ? "auto" : "hidden";
  }, [query, autoGrow, maxAutoGrowPx]);

  return (
    <div
      className={`sb-wrap ${isDragging ? "is-dragging" : ""}`}
      ref={shellRef}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {/* Mini thumbnails above the bar (can be hidden by parent) */}
      {showMiniThumbs && totalCount > 0 && (
        <div className="sb-minithumbs" role="list" aria-label="Attachments">
          {attachments.images.map((a, i) => (
            <div
              className="sb-minichip"
              role="listitem"
              key={`mimg-${i}`}
              title={a.file.name}
              style={{ overflow: "visible" }}
            >
              <img src={a.url} alt={a.file.name} />
              <button
                className="sb-mini-x"
                type="button"
                aria-label={`Remove ${a.file.name}`}
                onClick={() => removeOne("images", i)}
                title="Remove"
              >
                <FaTimes />
              </button>
            </div>
          ))}

          {attachments.audio.map((a, i) => (
            <div
              className="sb-minichip icon"
              role="listitem"
              key={`maud-${i}`}
              title={a.file.name}
              style={{ overflow: "visible" }}
            >
              <FaMicrophone />
              <button
                className="sb-mini-x"
                type="button"
                aria-label={`Remove ${a.file.name}`}
                onClick={() => removeOne("audio", i)}
                title="Remove"
              >
                <FaTimes />
              </button>
            </div>
          ))}

          {attachments.docs.map((a, i) => (
            <div
              className="sb-minichip icon"
              role="listitem"
              key={`mdoc-${i}`}
              title={a.file.name}
              style={{ overflow: "visible" }}
            >
              <div className="sb-mini-ext">{a.ext}</div>
              <button
                className="sb-mini-x"
                type="button"
                aria-label={`Remove ${a.file.name}`}
                onClick={() => removeOne("docs", i)}
                title="Remove"
              >
                <FaTimes />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* AURA pill */}
      <form className="sb-shell ai-aura" onSubmit={handleSubmit} aria-label="LLM search bar">
        <FaSearch className="sb-icon sb-search" aria-hidden />

        {/* Auto-growing textarea */}
        <textarea
          ref={inputRef}
          className="sb-input"
          rows={1}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          aria-label="Search or ask"
          autoComplete="off"
          onKeyDown={(e) => {
            // Enter to send (Shift+Enter makes a newline)
            if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          // When autoGrow is disabled, keep a sensible fixed height and allow manual scroll
          style={!autoGrow ? { height: 44, overflowY: "auto" } : undefined}
        />

        <div className="sb-actions">
          <button
            type="button"
            className="sb-iconbtn has-tip"
            data-tip="Upload image"
            onClick={() => imageInputRef.current?.click()}
            disabled={disabled || remainingSlots === 0}
            aria-label="Attach images"
            title="Upload image"
          >
            <FaImage />
          </button>
          <button
            type="button"
            className="sb-iconbtn has-tip"
            data-tip="Upload audio"
            onClick={() => audioInputRef.current?.click()}
            disabled={disabled || remainingSlots === 0}
            aria-label="Attach audio"
            title="Upload audio"
          >
            <FaMicrophone />
          </button>
          <button
            type="button"
            className="sb-iconbtn has-tip"
            data-tip="Upload document"
            onClick={() => docInputRef.current?.click()}
            disabled={disabled || remainingSlots === 0}
            aria-label="Attach documents"
            title="Upload document"
          >
            <FaPaperclip />
          </button>

          {totalCount > 0 && (
            <button
              type="button"
              className="sb-iconbtn danger has-tip"
              data-tip="Remove all attachments"
              onClick={clearAll}
              aria-label="Remove all attachments"
              title="Remove all attachments"
            >
              <FaTrashAlt />
            </button>
          )}

          <button
            className="sb-ask has-tip"
            data-tip="Send (Ctrl/Cmd + Enter)"
            type="submit"
            disabled={disabled || busy}
            aria-label="Submit"
            aria-busy={busy ? "true" : "false"}
            title="Send (Ctrl/Cmd + Enter)"
          >
            {busy ? "Thinking…" : "Ask"}
          </button>
        </div>

        {/* Hidden pickers */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onPickImages}
          hidden
        />
        <input
          ref={audioInputRef}
          type="file"
          accept="audio/*"
          multiple
          onChange={onPickAudio}
          hidden
        />
        <input
          ref={docInputRef}
          type="file"
          accept={acceptDocs}
          multiple
          onChange={onPickDocs}
          hidden
        />
      </form>
    </div>
  );
}
